import api from './api';

export const authService = {
  // Register new personnel (admin only)
  registerPersonnel: async (payload) => {
    const response = await api.post('/auth/register', payload);
    return response.data;
  },
  // Invite new personnel (admin only)
  invitePersonnel: async (payload) => {
    const response = await api.post('/auth/invite', payload);
    return response.data;
  },
  // Forgot password (public)
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },
  // Reset password with token (public)
  resetPassword: async (token, email, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, email, newPassword });
    return response.data;
  },
  // Admin reset a user's password
  adminResetPassword: async (personnelId, newPassword) => {
    const response = await api.put(`/auth/admin/reset-password/${personnelId}`, { newPassword });
    return response.data;
  },
  // MFA setup
  mfaSetup: async () => {
    const response = await api.post('/auth/mfa/setup');
    return response.data;
  },
  // MFA verify
  mfaVerify: async (token) => {
    const response = await api.post('/auth/mfa/verify', { token });
    return response.data;
  },
  // MFA disable
  mfaDisable: async (password) => {
    const response = await api.post('/auth/mfa/disable', { password });
    return response.data;
  },
  // Get current user profile (includes loginHistory)
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  }
};

export const caseService = {
  // Get all cases
  getCases: async (params = {}) => {
    const response = await api.get('/cases', { params });
    return response.data;
  },

  // Get charge suggestions
  getChargeSuggestions: async (query = '', limit = 20) => {
    const response = await api.get('/cases/charges/suggestions', { params: { query, limit } });
    return response.data;
  },

  // Get single case
  getCase: async (id) => {
    const response = await api.get(`/cases/${id}`);
    return response.data;
  },

  // Create new case
  createCase: async (caseData) => {
    const response = await api.post('/cases', caseData);
    return response.data;
  },

  // Update case
  updateCase: async (id, caseData) => {
    const response = await api.put(`/cases/${id}`, caseData);
    return response.data;
  },

  // Request charge change
  requestChargeChange: async (id, charges, reviewNotes = '') => {
    const response = await api.post(`/cases/${id}/charges/request`, { charges, reviewNotes });
    return response.data;
  },

  // Get charge change requests for a case
  getChargeChangeRequests: async (id) => {
    const response = await api.get(`/cases/${id}/charges/requests`);
    return response.data;
  },

  // Approve charge change request
  approveChargeChange: async (requestId, reviewNotes = '') => {
    const response = await api.patch(`/cases/charges/requests/${requestId}/approve`, { reviewNotes });
    return response.data;
  },

  // Reject charge change request
  rejectChargeChange: async (requestId, reviewNotes = '') => {
    const response = await api.patch(`/cases/charges/requests/${requestId}/reject`, { reviewNotes });
    return response.data;
  },

  // Delete case
  deleteCase: async (id) => {
    const response = await api.delete(`/cases/${id}`);
    return response.data;
  },

  // Add note to case
  addNote: async (id, noteData) => {
    const response = await api.post(`/cases/${id}/notes`, noteData);
    return response.data;
  },

  // Change case status (enforced workflow)
  changeStatus: async (id, status, note = '', closureReason = '') => {
    const response = await api.post(`/cases/${id}/status`, { status, note, closureReason });
    return response.data;
  },

  // Get status transition history
  getStatusHistory: async (id) => {
    const response = await api.get(`/cases/${id}/status-history`);
    return response.data;
  },

  // Bulk update status for multiple cases
  bulkUpdateStatus: async (caseIds, status, note = '') => {
    const response = await api.patch('/cases/bulk-status', { caseIds, status, note });
    return response.data;
  },

  // Update suspects/victims/witnesses on a case
  updateCasePeople: async (id, people) => {
    const response = await api.patch(`/cases/${id}/people`, people);
    return response.data;
  }
};

export const evidenceService = {
  // Get all evidence
  getEvidence: async (params = {}) => {
    const response = await api.get('/evidence', { params });
    return response.data;
  },

  // Get single evidence
  getEvidenceItem: async (id) => {
    const response = await api.get(`/evidence/${id}`);
    return response.data;
  },

  // Create evidence
  createEvidence: async (evidenceData) => {
    const response = await api.post('/evidence', evidenceData);
    return response.data;
  },

  // Update evidence
  updateEvidence: async (id, evidenceData) => {
    const response = await api.put(`/evidence/${id}`, evidenceData);
    return response.data;
  },

  // Transfer evidence
  transferEvidence: async (id, transferData) => {
    const response = await api.post(`/evidence/${id}/transfer`, transferData);
    return response.data;
  },

  // Delete evidence
  deleteEvidence: async (id) => {
    const response = await api.delete(`/evidence/${id}`);
    return response.data;
  }
};

export const documentService = {
  // Get all documents
  getDocuments: async (params = {}) => {
    const response = await api.get('/documents', { params });
    return response.data;
  },

  // Get single document
  getDocument: async (id) => {
    const response = await api.get(`/documents/${id}`);
    return response.data;
  },

  // Upload document
  uploadDocument: async (formData) => {
    const response = await api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Download document
  downloadDocument: async (id) => {
    const response = await api.get(`/documents/${id}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Delete document
  deleteDocument: async (id) => {
    const response = await api.delete(`/documents/${id}`);
    return response.data;
  },

  // Request signature
  requestSignature: async (id, personnelId) => {
    const response = await api.post(`/documents/${id}/signature/request`, { personnelId });
    return response.data;
  },

  // Sign document
  signDocument: async (id) => {
    const response = await api.post(`/documents/${id}/signature/sign`);
    return response.data;
  },

  // Decline signature
  declineSignature: async (id) => {
    const response = await api.post(`/documents/${id}/signature/decline`);
    return response.data;
  },

  // Get shared-with list
  getSharedWith: async (id) => {
    const response = await api.get(`/documents/${id}/shared-with`);
    return response.data;
  },

  // Get access log
  getAccessLog: async (id) => {
    const response = await api.get(`/documents/${id}/access-log`);
    return response.data;
  },

  // Share document
  shareDocument: async (id, payload) => {
    const response = await api.post(`/documents/${id}/share`, payload);
    return response.data;
  },

  // Revoke access
  revokeShare: async (id, personnelId) => {
    const response = await api.delete(`/documents/${id}/share/${personnelId}`);
    return response.data;
  },

  // Assign/unassign document to case
  updateDocumentCase: async (id, caseId) => {
    const response = await api.patch(`/documents/${id}/case`, { caseId });
    return response.data;
  },

  // Upload a new version of an existing document
  uploadVersion: async (id, formData) => {
    const response = await api.post(`/documents/${id}/version`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Get version history for a document
  getVersionHistory: async (id) => {
    const response = await api.get(`/documents/${id}/versions`);
    return response.data;
  }
};

export const personnelService = {
  // Get distinct department names within the agency
  getDepartments: async () => {
    const response = await api.get('/personnel/departments');
    return response.data;
  },

  // Get all personnel
  getPersonnel: async (params = {}) => {
    const response = await api.get('/personnel', { params });
    return response.data;
  },

  // Get single personnel
  getPersonnelMember: async (id) => {
    const response = await api.get(`/personnel/${id}`);
    return response.data;
  },

  // Update personnel
  updatePersonnel: async (id, personnelData) => {
    const response = await api.put(`/personnel/${id}`, personnelData);
    return response.data;
  },

  // Get current user's signature
  getMySignature: async () => {
    const response = await api.get('/personnel/me/signature');
    return response.data;
  },

  // Save current user's signature
  saveMySignature: async (dataUrl) => {
    const response = await api.put('/personnel/me/signature', { dataUrl });
    return response.data;
  },

  // Update permissions
  updatePermissions: async (id, permissions) => {
    const response = await api.put(`/personnel/${id}/permissions`, permissions);
    return response.data;
  },

  // Deactivate personnel
  deactivatePersonnel: async (id) => {
    const response = await api.put(`/personnel/${id}/deactivate`);
    return response.data;
  },

  // Activate personnel
  activatePersonnel: async (id) => {
    const response = await api.put(`/personnel/${id}/activate`);
    return response.data;
  },

  // Delete personnel
  deletePersonnel: async (id) => {
    const response = await api.delete(`/personnel/${id}`);
    return response.data;
  },

  // Get permission templates
  getPermissionTemplates: async () => {
    const response = await api.get('/personnel/permission-templates');
    return response.data;
  },

  // Apply permission template to a personnel record
  applyPermissionTemplate: async (id, template) => {
    const response = await api.post(`/personnel/${id}/apply-template`, { template });
    return response.data;
  },

  // Update current user's duty status
  updateDutyStatus: async (dutyStatus) => {
    const response = await api.patch('/personnel/duty-status', { dutyStatus });
    return response.data;
  },

  // Get duty roster for the department
  getDutyRoster: async () => {
    const response = await api.get('/personnel/duty-roster');
    return response.data;
  }
};

export const timelineService = {
  // Get timeline events
  getTimeline: async (params = {}) => {
    const response = await api.get('/timeline', { params });
    return response.data;
  },

  // Get single event
  getTimelineEvent: async (id) => {
    const response = await api.get(`/timeline/${id}`);
    return response.data;
  },

  // Create event
  createEvent: async (eventData) => {
    const response = await api.post('/timeline', eventData);
    return response.data;
  },

  // Update event
  updateEvent: async (id, eventData) => {
    const response = await api.put(`/timeline/${id}`, eventData);
    return response.data;
  },

  // Verify event
  verifyEvent: async (id) => {
    const response = await api.put(`/timeline/${id}/verify`);
    return response.data;
  },

  // Delete event
  deleteEvent: async (id) => {
    const response = await api.delete(`/timeline/${id}`);
    return response.data;
  }
};

export const notificationService = {
  // Get notifications
  getNotifications: async (params = {}) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  // Mark as read
  markAsRead: async (id) => {
    const response = await api.put(`/notifications/${id}/read`);
    return response.data;
  },

  // Mark all as read
  markAllAsRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },

  // Delete notification
  deleteNotification: async (id) => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  }
};

export const searchService = {
  // Quick search (alias for global search, used by navbar)
  search: async (query) => {
    const response = await api.post('/search/global', { query });
    return response.data;
  },

  // Global search
  globalSearch: async (query) => {
    const response = await api.post('/search/global', { query });
    return response.data;
  },

  // Advanced search
  advancedSearch: async (payloadOrType, filters = {}, page = 1) => {
    const body = typeof payloadOrType === 'object'
      ? payloadOrType
      : {
          searchType: payloadOrType,
          filters,
          page
        };
    const response = await api.post('/search/advanced', body);
    return response.data;
  },

  // Save search
  saveSearch: async (searchData) => {
    const response = await api.post('/search/save', searchData);
    return response.data;
  },

  // Get saved searches
  getSavedSearches: async () => {
    const response = await api.get('/search/saved');
    return response.data;
  },

  // Delete saved search
  deleteSavedSearch: async (id) => {
    const response = await api.delete(`/search/saved/${id}`);
    return response.data;
  }
};

export const taskService = {
  // Get tasks
  getTasks: async (params = {}) => {
    const response = await api.get('/tasks', { params });
    return response.data;
  },

  // Get my tasks
  getMyTasks: async (status = null) => {
    const response = await api.get('/tasks/my-tasks', {
      params: status ? { status } : {}
    });
    return response.data;
  },

  // Create task
  createTask: async (taskData) => {
    const response = await api.post('/tasks', taskData);
    return response.data;
  },

  // Update task
  updateTask: async (id, taskData) => {
    const response = await api.put(`/tasks/${id}`, taskData);
    return response.data;
  },

  // Delete task
  deleteTask: async (id) => {
    const response = await api.delete(`/tasks/${id}`);
    return response.data;
  }
};

export const analyticsService = {
  // Get dashboard analytics
  getDashboardAnalytics: async (params = {}) => {
    const response = await api.get('/analytics/dashboard', {
      params
    });
    return response.data;
  },

  // Get case metrics
  getCaseMetrics: async (startDate = null, endDate = null) => {
    const response = await api.get('/analytics/case-metrics', {
      params: { startDate, endDate }
    });
    return response.data;
  },

  // Get activity feed
  getActivityFeed: async (limit = 50) => {
    const response = await api.get('/analytics/activity-feed', {
      params: { limit }
    });
    return response.data;
  },

  // Get lightweight stats for dashboard (no analytics permission required)
  getStats: async () => {
    const response = await api.get('/analytics/stats');
    return response.data;
  },

  // Generate case report
  generateCaseReport: async (caseId) => {
    const response = await api.get(`/analytics/reports/case/${caseId}`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

export const exportService = {
  // Export cases as PDF
  exportCasesPDF: async (filters = {}, options = {}) => {
    const response = await api.get('/export/cases/pdf', {
      params: filters,
      headers: options.reason ? { 'X-Export-Reason': options.reason } : undefined,
      responseType: 'blob'
    });
    return response.data;
  },

  // Download case file package (report + documents)
  downloadCaseFilePackage: async (caseId, params = {}, options = {}) => {
    const response = await api.get(`/export/cases/${caseId}/package`, {
      params,
      headers: options.reason ? { 'X-Export-Reason': options.reason } : undefined,
      responseType: 'blob'
    });
    return response.data;
  },

  // Export cases as Excel
  exportCasesExcel: async (filters = {}, options = {}) => {
    const response = await api.get('/export/cases/excel', {
      params: filters,
      headers: options.reason ? { 'X-Export-Reason': options.reason } : undefined,
      responseType: 'blob'
    });
    return response.data;
  },

  // Export cases as CSV
  exportCasesCSV: async (filters = {}, options = {}) => {
    const response = await api.get('/export/cases/csv', {
      params: filters,
      headers: options.reason ? { 'X-Export-Reason': options.reason } : undefined,
      responseType: 'blob'
    });
    return response.data;
  },

  // Export evidence as Excel
  exportEvidenceExcel: async (filters = {}, options = {}) => {
    const response = await api.get('/export/evidence/excel', {
      params: filters,
      headers: options.reason ? { 'X-Export-Reason': options.reason } : undefined,
      responseType: 'blob'
    });
    return response.data;
  },

  // Export chain of custody
  exportChainOfCustody: async (evidenceId, options = {}) => {
    const response = await api.get(`/export/evidence/${evidenceId}/chain-of-custody`, {
      headers: options.reason ? { 'X-Export-Reason': options.reason } : undefined,
      responseType: 'blob'
    });
    return response.data;
  },

  // Print-friendly case summary (HTML blob)
  printCaseSummary: async (caseId) => {
    const response = await api.get(`/analytics/reports/case/${caseId}`, { responseType: 'blob' });
    return response.data;
  },

  // Download file helper
  downloadFile: (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};

export const caseRequestService = {
  // Get case requests
  getRequests: async (params = {}) => {
    const response = await api.get('/case-requests', { params });
    return response.data;
  },

  // Get casefile view details
  getCasefileView: async (id) => {
    const response = await api.get(`/case-requests/${id}/casefile-view`);
    return response.data;
  },

  // Get single request
  getRequest: async (id) => {
    const response = await api.get(`/case-requests/${id}`);
    return response.data;
  },

  // Create request
  createRequest: async (payload) => {
    const response = await api.post('/case-requests', payload);
    return response.data;
  },

  // Update request
  updateRequest: async (id, payload) => {
    const response = await api.put(`/case-requests/${id}`, payload);
    return response.data;
  },

  // Delete request
  deleteRequest: async (id) => {
    const response = await api.delete(`/case-requests/${id}`);
    return response.data;
  },

  // Add note
  addNote: async (id, content) => {
    const response = await api.post(`/case-requests/${id}/notes`, { content });
    return response.data;
  },

  // Log signature usage
  logSignatureUse: async (id, payload = {}) => {
    const response = await api.post(`/case-requests/${id}/signature-use`, payload);
    return response.data;
  }
};

export const locationService = {
  geocodeAddress: async (payload) => {
    try {
      const response = await api.post('/locations/geocode', payload);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: error.response?.data?.message || 'No geocoding results found'
        };
      }
      throw error;
    }
  }
};

export const auditService = {
  getLogs: async (params = {}) => {
    const response = await api.get('/audit', { params });
    return response.data;
  },
  getCategories: async () => {
    const response = await api.get('/audit/categories');
    return response.data;
  }
};

export const reportService = {
  // Templates
  getTemplates: async () => { const r = await api.get('/reports/templates'); return r.data; },
  createTemplate: async (data) => { const r = await api.post('/reports/templates', data); return r.data; },
  updateTemplate: async (id, data) => { const r = await api.put(`/reports/templates/${id}`, data); return r.data; },
  deleteTemplate: async (id) => { const r = await api.delete(`/reports/templates/${id}`); return r.data; },
  // Schedules
  getSchedules: async () => { const r = await api.get('/reports/schedules'); return r.data; },
  createSchedule: async (data) => { const r = await api.post('/reports/schedules', data); return r.data; },
  updateSchedule: async (id, data) => { const r = await api.put(`/reports/schedules/${id}`, data); return r.data; },
  deleteSchedule: async (id) => { const r = await api.delete(`/reports/schedules/${id}`); return r.data; },
  runSchedule: async (id) => { const r = await api.post(`/reports/schedules/${id}/run`); return r.data; },
  getRuns: async () => { const r = await api.get('/reports/runs'); return r.data; }
};

export const ownerService = {
  getAgencies: async () => { const r = await api.get('/owner/agencies'); return r.data; },
  suspendAgency: async (id, reason) => { const r = await api.post(`/owner/agencies/${id}/suspend`, { reason }); return r.data; },
  unsuspendAgency: async (id) => { const r = await api.post(`/owner/agencies/${id}/unsuspend`); return r.data; },
  updateAgency: async (id, data) => { const r = await api.put(`/owner/agencies/${id}`, data); return r.data; },
  getStats: async () => { const r = await api.get('/owner/stats'); return r.data; }
};

export const commentService = {
  getComments: async (entityType, entityId) => {
    const r = await api.get(`/comments/${entityType}/${entityId}`);
    return r.data;
  },
  createComment: async (data) => {
    const r = await api.post('/comments', data);
    return r.data;
  },
  deleteComment: async (id) => {
    const r = await api.delete(`/comments/${id}`);
    return r.data;
  },
  replyToComment: async (id, content) => {
    const r = await api.post(`/comments/${id}/reply`, { content });
    return r.data;
  }
};
